import { supabase } from './supabase';

export type DashboardEventCategory =
  | 'preset'
  | 'breathing_style'
  | 'ratio'
  | 'video'
  | 'audio'
  | 'affirmation'
  | 'duration'
  | 'button'
  | 'custom';

type JsonValue = string | number | boolean | null | Record<string, unknown>;
export type DashboardEventValue = JsonValue;

export interface DashboardEventPayload {
  eventType: string;
  eventCategory: DashboardEventCategory | string;
  eventLabel?: string | null;
  eventValue?: JsonValue;
  visitorId: string;
  sessionId: string;
  userId?: string | null;
  page?: string;
}

export interface DashboardEventRow {
  id: string;
  visitor_id: string;
  user_id: string | null;
  session_id: string;
  event_type: string;
  event_category: string;
  event_label: string | null;
  event_value: string | null;
  page: string | null;
  created_at: string;
  display_name?: string | null;
  is_admin?: boolean;
}

const TABLE_NAME = 'dashboard_events';

const serializeEventValue = (value?: JsonValue): string | null => {
  if (value === undefined || value === null) return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch {
    return null;
  }
};

export async function logDashboardEvent(
  payload: DashboardEventPayload
): Promise<void> {
  const { error } = await supabase.from(TABLE_NAME).insert({
    visitor_id: payload.visitorId,
    user_id: payload.userId ?? null,
    session_id: payload.sessionId,
    event_type: payload.eventType,
    event_category: payload.eventCategory,
    event_label: payload.eventLabel ?? null,
    event_value: serializeEventValue(payload.eventValue),
    page: payload.page ?? 'dashboard',
  });

  if (error) {
    throw error;
  }
}

export async function fetchDashboardEvents(
  limit = 200
): Promise<DashboardEventRow[]> {
  const { data, error } = await supabase
    .from(TABLE_NAME)
    .select(
      'id, visitor_id, user_id, session_id, event_type, event_category, event_label, event_value, page, created_at'
    )
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    throw error;
  }

  const rows = (data as DashboardEventRow[]) ?? [];

  const userIds = Array.from(
    new Set(rows.map((row) => row.user_id).filter(Boolean) as string[])
  );

  if (userIds.length > 0) {
    const [settingsResult, rolesResult] = await Promise.all([
      supabase
        .from('user_settings')
        .select('user_id, display_name')
        .in('user_id', userIds),
      supabase
        .from('user_roles')
        .select('user_id, role')
        .in('user_id', userIds),
    ]);

    if (!settingsResult.error && settingsResult.data) {
      const displayMap = new Map<string, string | null>();
      settingsResult.data.forEach((entry: any) => {
        if (entry?.user_id) {
          displayMap.set(entry.user_id, entry.display_name ?? null);
        }
      });

      rows.forEach((row) => {
        if (row.user_id) {
          row.display_name = displayMap.get(row.user_id) ?? null;
        }
      });
    }

    if (!rolesResult.error && rolesResult.data) {
      const adminSet = new Set(
        rolesResult.data
          .filter(
            (entry: any) =>
              typeof entry?.role === 'string' &&
              entry.role.toLowerCase() === 'admin'
          )
          .map((entry: any) => entry.user_id)
      );

      rows.forEach((row) => {
        if (row.user_id) {
          row.is_admin = adminSet.has(row.user_id);
        }
      });
    }
  }

  return rows;
}
